import { useMutation, useQuery } from "@apollo/client";
import { Button, InputItem } from "pepsico-ds";
import { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DeleteConfirmationModal from "../../components/common/DeleteConfirmationModal/DeleteConfirmationModal";
import { ListingComponentContext } from "../../components/common/listingComponents/ListingComponentContext";
import ToastNotification from "../../components/common/toastNotification/ToastNotification";
import AvailableSurveysListComponent from "../../components/surveyManagement/AvailableSurveysListComponent";
import SurveyDetailsDrawer from "../../components/surveyManagement/SurveyDetailsDrawer";
import { EMPTY_SURVEY } from "../../components/surveyManagement/surveyDrawerUtils";
import { useSurveyProviders } from "../../components/surveyManagement/useSurveyProviders";
import { mapSurveyToRow } from "../../components/surveyManagement/surveyRowMapper";
import {
  createSurvey,
  deleteSurvey,
  toSurveyDefinitionInput,
  updateSurvey,
} from "../../graphql/mutations/surveyManagementMutations";
import { GET_SURVEYS } from "../../graphql/queries/getSurveys";
import { useToast } from "../../hooks/useToast";
import { buildSurveyFilterConfig } from "./surveyFilterConfig";
import ListingComponentWrapper from "../../components/common/listingComponents/ListingComponentWrapper";
import "../../components/surveyManagement/surveyManagement.scss";

const CLOSED_DRAWER = {
  open: false,
  mode: "create",
  initialValues: EMPTY_SURVEY,
  platformId: null,
};

const SurveyManagementListPage = () => {
  const { t } = useTranslation();
  const providers = useSurveyProviders();
  const filterConfig = useMemo(
    () => buildSurveyFilterConfig(providers),
    [providers]
  );
  const [nameSearch, setNameSearch] = useState("");
  const [drawer, setDrawer] = useState(CLOSED_DRAWER);
  const [saving, setSaving] = useState(false);
  // Holds the row pending deletion; non-null opens the confirmation modal.
  const [deleteTarget, setDeleteTarget] = useState(null);

  // refetch lives on the shared listing context (provided by SurveyManagementOutlet),
  // so a create/edit can refresh the list without a full navigation.
  const refetch = useContext(ListingComponentContext)?.refetch;
  const { toast, showToast, closeToast } = useToast();

  // Full, unfiltered survey set for the drawer's Survey ID / Survey URL uniqueness
  // check. The listing context is server-filtered (Source/Survey-ID map to the BFF
  // filter and refetch), so it can be a subset — the duplicate check must see all
  // surveys. Refetched after each successful save so new rows are counted.
  const { data: allSurveysData, refetch: refetchAllSurveys } = useQuery(
    GET_SURVEYS,
    { variables: { filter: {} }, fetchPolicy: "cache-and-network" }
  );
  const existingSurveys = (allSurveysData?.getSurveyDefinitions?.items || []).map(
    mapSurveyToRow
  );

  const [createSurveyMutation] = useMutation(createSurvey);
  const [updateSurveyMutation] = useMutation(updateSurvey);
  const [deleteSurveyMutation] = useMutation(deleteSurvey);

  const openCreate = () =>
    setDrawer({
      open: true,
      mode: "create",
      initialValues: EMPTY_SURVEY,
      platformId: null,
    });

  // row is a mapSurveyToRow output: surveyId is the 3P partnerSurveyId shown in the drawer,
  // while the update mutation keys off the platform surveyId (row.original.surveyId).
  const openEdit = (row) =>
    setDrawer({
      open: true,
      mode: "edit",
      initialValues: {
        source: row.source,
        surveyName: row.surveyName,
        surveyId: row.surveyId,
        surveyUrl: row.surveyUrl,
      },
      platformId: row.original?.surveyId ?? null,
    });

  const closeDrawer = () => setDrawer(CLOSED_DRAWER);

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const input = toSurveyDefinitionInput(values);
      if (drawer.mode === "edit") {
        const res = await updateSurveyMutation({
          variables: { id: drawer.platformId, input },
        });
        const err = res?.data?.updateSurveyDefinition?.error;
        if (err) throw new Error(err.message);
      } else {
        const res = await createSurveyMutation({ variables: { input } });
        const err = res?.data?.createSurveyDefinition?.error;
        if (err) throw new Error(err.message);
      }
      showToast(
        "success",
        drawer.mode === "edit"
          ? t("SurveyManagement:UpdateSuccessTitle")
          : t("SurveyManagement:CreateSuccessTitle"),
        ""
      );
      closeDrawer();
      // Refresh the listing and the uniqueness set in parallel; the just-saved
      // survey must be counted the next time the drawer opens.
      await Promise.all([refetch?.(), refetchAllSurveys?.()]);
    } catch (e) {
      showToast("error", t("SurveyManagement:SaveErrorTitle"), e?.message ?? "");
    } finally {
      setSaving(false);
    }
  };

  // row is a mapSurveyToRow output; deletion keys off the platform surveyId
  // (row.original.surveyId), matching the BFF deleteSurveyDefinition contract.
  const openDelete = (row) => setDeleteTarget(row);
  const closeDelete = () => setDeleteTarget(null);

  const handleConfirmDelete = async () => {
    const id = deleteTarget?.original?.surveyId;
    try {
      const res = await deleteSurveyMutation({ variables: { id } });
      // deleteSurveyDefinition returns false when the survey no longer exists
      // (the BFF maps a not-found to false rather than a non-null GraphQL error).
      if (res?.data?.deleteSurveyDefinition !== true) {
        throw new Error(t("SurveyManagement:DeleteNotFoundDescription"));
      }
      showToast("success", t("SurveyManagement:DeleteSuccessTitle"), "");
      closeDelete();
      // Refresh the listing and the uniqueness set so a deleted survey stops
      // counting as a duplicate when its Survey ID/URL is recreated.
      await Promise.all([refetch?.(), refetchAllSurveys?.()]);
    } catch (e) {
      showToast("error", t("SurveyManagement:DeleteErrorTitle"), e?.message ?? "");
    }
  };

  return (
    <div className="px-3 pt-3 survey-management-page">
      <div className="survey-management-card">
        <div className="page-header">
          <h6 className="font-bold font-lg">Available Surveys</h6>
          <Button iconTrailing="add_circle" size="medium" onClick={openCreate}>
            {t("SurveyManagement:AddSurvey")}
          </Button>
        </div>
        <div className="survey-search">
          <InputItem
            placeholder="Search By Survey Name"
            trailingIcon="search"
            size="medium"
            value={nameSearch}
            onChange={(e) => setNameSearch(e.target.value)}
          />
        </div>
        <ListingComponentWrapper filterConfig={filterConfig}>
          <AvailableSurveysListComponent
            nameSearch={nameSearch}
            onEdit={openEdit}
            onDelete={openDelete}
          />
        </ListingComponentWrapper>
      </div>

      <SurveyDetailsDrawer
        open={drawer.open}
        mode={drawer.mode}
        initialValues={drawer.initialValues}
        existingSurveys={existingSurveys}
        editingPlatformId={drawer.platformId}
        onSave={handleSave}
        onCancel={closeDrawer}
        saving={saving}
      />

      <DeleteConfirmationModal
        isOpen={!!deleteTarget}
        title={t("SurveyManagement:DeleteConfirmTitle")}
        confirmText={t("SurveyManagement:DeleteConfirm")}
        cancelText={t("SurveyManagement:Cancel")}
        onConfirm={handleConfirmDelete}
        onCancel={closeDelete}
      />

      <ToastNotification
        {...toast}
        handleClose={closeToast}
        primaryActionLabel="Close"
      />
    </div>
  );
};

export default SurveyManagementListPage;
